import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { startOfDay, subDays, endOfDay } from 'date-fns'
import DatePicker from 'react-datepicker'
import { debounce } from 'lodash'
import { fromZonedTime } from 'date-fns-tz'
import { useTranslation } from 'react-i18next'

import { fetchLoyaltyLeaderboard, selectLeaderboard } from '../../../../../reducers/loyaltyReducer'
import SearchField from '../../../../FormLegacy/FormFieldsV2/SearchField'
import FeaturedTable from '../../../../FeaturedTable/FeaturedTable'
import PaginationFooter from '../../../../PaginationFooter/PaginationFooter'
import { leaderBoardColumns } from '../../helpers'
import { useSnackbar } from '../../../../../helpers/hooks/useSnackbar'

const DEFAULT_DAYS_RANGE = 30
const INITIAL_DATE_FILTER = [
  subDays(startOfDay(Date.now()), DEFAULT_DAYS_RANGE),
  startOfDay(Date.now()),
]

const LeaderBoardTable = ({
  building,
  leaderboard = [],
  onLoyaltyProfile = () => {},
  onRewardsHistory = () => {},
  fetchLoyaltyLeaderboard = () => {},
}) => {
  const { t } = useTranslation(['rewards-dashboard'])
  const [search, setSearch] = useState('')
  const [isLoading, setLoading] = useState(false)
  const [showOnPage, setShowOnPage] = useState(25)
  const [pageNumber, setPageNumber] = useState(1)
  const [paginationItemsCount, setPaginationItemsCount] = useState(0)
  const [dateFilter, setDateFilter] = useState(INITIAL_DATE_FILTER)
  const [prevSearchValue, setPrevSearchValue] = useState('')
  const [startDate, endDate] = dateFilter
  const { displaySnackbar } = useSnackbar()

  const onSearchChange = (e) => {
    setPageNumber(1)
    setSearch(e.target.value)
  }

  const fetchLeaderboard = async (query) => {
    setLoading(true)

    if (query === undefined) {
      query = search
    }

    const requestData = {
      buildingId: building?.id,
      page: query ? 1 : pageNumber,
      limit: showOnPage,
      query,
      startDate: startDate && endDate ? fromZonedTime(startDate, 'UTC').getTime() : undefined,
      endDate: endDate ? fromZonedTime(endOfDay(endDate), 'UTC').getTime() : undefined,
    }

    await fetchLoyaltyLeaderboard(requestData)
      .then((count) => setPaginationItemsCount(count))
      .catch(({ message }) => displaySnackbar(message, true))

    setLoading(false)
  }

  useEffect(() => {
    if (building) {
      fetchLeaderboard()
    }
  }, [pageNumber, showOnPage, dateFilter, building])

  const debouncedFetch = debounce(fetchLeaderboard, 800)

  useEffect(() => {
    if (prevSearchValue !== search) {
      window.addEventListener('keydown', debouncedFetch(search))
    }
    setPrevSearchValue(search)
    return () => {
      window.removeEventListener('keydown', debouncedFetch)
    }
  }, [search])

  return (
    <div className="rewards-container">
      <div className="rewards-table">
        <div className="rewards-table__wrap">
          <div className="rewards-table__filters">
            <div className="rewards-table__filters__group">
              <span className="rewards-dashboard__title">
                {t('rewards-dashboard:leaderBoard.title')}
              </span>
            </div>
            <div className="rewards-table__filters__group">
              <div className={'rewards-table__filters__search'}>
                <SearchField
                  placeholder={t('rewards-dashboard:leaderBoard.searchPlaceholder')}
                  value={search}
                  onChange={onSearchChange}
                />
                <DatePicker
                  className={'input borderless'}
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  maxDate={new Date()}
                  placeholderText={t('rewards-dashboard:leaderBoard.selectDateRangePlaceholder')}
                  isClearable={true}
                  onChange={setDateFilter}
                />
              </div>
            </div>
          </div>
          <div className={`ctp-leader-board-list ${!isLoading ? '' : 'is-loading'}`}>
            {!isLoading && (
              <React.Fragment>
                {leaderboard.length > 0 && building ? (
                  <FeaturedTable
                    columns={leaderBoardColumns({
                      onLoyaltyProfile,
                      onRewardsHistory,
                      t,
                    })}
                    data={leaderboard}
                  />
                ) : (
                  t('rewards-dashboard:leaderBoard.noResultsYet')
                )}
              </React.Fragment>
            )}
          </div>
          {leaderboard.length > 0 && (
            <PaginationFooter
              showOnPage={showOnPage}
              pageNumber={pageNumber}
              paginationItemsCount={paginationItemsCount}
              setSiteLoading={setLoading}
              setShowOnPage={setShowOnPage}
              setPageNumber={setPageNumber}
            />
          )}
        </div>
      </div>
    </div>
  )
}

LeaderBoardTable.propTypes = {
  leaderboard: PropTypes.array,
  building: PropTypes.object,
  onLoyaltyProfile: PropTypes.func,
  onRewardsHistory: PropTypes.func,
  fetchLoyaltyLeaderboard: PropTypes.func,
}

export default connect(
  (state) => ({
    leaderboard: selectLeaderboard(state),
  }),
  {
    fetchLoyaltyLeaderboard,
  },
)(LeaderBoardTable)
